import type { FC } from 'react';
import { vegaCopyConversation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { DateFormat } from '@gonfalon/format/constants';
import { format } from '@gonfalon/format/format';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';

import { useAgentInsights } from '../../../../AgentInsightsContext';
import { extractResultText } from '../../../../agentInsightsUtils';
import { trackVegaAiMessageCopied } from '../../../../vegaAnalytics';
import { useAgentResponseSelector } from '../../hooks/useAgentResponseSelector';
import { VegaMarkdownRenderer } from '../artifacts/VegaMarkdownRenderer';
import { CopyButton } from '../CopyButton';

import { copyAffordance, copyHoverGroup } from '../CopyButton.css';

interface ResultMessageProps {
  message: any;
  timestamp?: string;
  onSetAgentResponse?: (response: string) => void;
}

// Component for rendering result messages
// these are final analysis results from the agent
export const ResultMessage: FC<ResultMessageProps> = ({ message, timestamp, onSetAgentResponse }) => {
  const { projectKey } = useProjectId();
  const { startConversationWithSource } = useAgentInsights();
  const { onTaskAction } = useAgentResponseSelector(onSetAgentResponse);
  const copyEnabled = vegaCopyConversation();

  const resultText = extractResultText(message.text);
  const formattedTimestamp = timestamp ? format(timestamp, DateFormat.MMM_D_H_MM_A) : null;

  if (!resultText) {
    return null;
  }

  return (
    <Box p="8" cssClass={copyEnabled ? copyHoverGroup : undefined}>
      <VegaMarkdownRenderer
        text={resultText}
        projectKey={projectKey}
        onExternalSourceClick={(match) => startConversationWithSource(match.url, match.sourceType)}
        onTaskAction={onSetAgentResponse ? onTaskAction : undefined}
      />
      {copyEnabled && (
        <Box mt="2" display="flex" alignItems="center" justifyContent="flex-start" gap="4" cssClass={copyAffordance}>
          <CopyButton
            text={resultText}
            label="Copy message"
            successTitle="Message copied to clipboard"
            onCopy={() => trackVegaAiMessageCopied('vega')}
          />
          {formattedTimestamp && (
            <Text size="xSmall" color="weak">
              {formattedTimestamp}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
